import { useState, type ReactNode } from "react";
import { MoreInformationSection } from "../sections/MoreInformationSection";
import type { RunRecordBodyProps } from "../../runRecord/RunRecordBody";
import type { DrawerSectionMap } from "./types";
import { FileUploadDropzone } from "~/common/components/FileUploadDropzone";
import {
  EMAIL_ATTACHABLE_MAX_BYTES,
  MAX_DOCUMENTS,
} from "~/common/workflows/components/CarrierEvidencePanel";
import HitlSideActions, {
  type SideActionResult,
} from "../../components/HitlSideActions";
import { renderInlineBold } from "../../renderInlineBold";
import { NEEDS_YOU_LABEL } from "~/common/workflows/statusLabels";

/**
 * PendingEdocsDrawerBody — the shared carrier-evidence decision surface for an
 * instance parked in ``pending_edocs``. Shared by ora's WorkflowDetailDrawer
 * and the Outlook plugin's InstanceDetailDrawer.
 *
 * ONE decision identity, TWO branches, both rendered from the SAME
 * ``hitlQuestion`` payload with no host-specific fork (design Constraint 20):
 *
 * - **carrier branch** (``evidence_source !== "am"``) — the calm "status, no
 *   action needed" card. The AM has nothing to do: the run resumes on its own
 *   when the carrier's documents land. Affordances are the "Check now" side
 *   action, a confirm-first Reject, and — new — a confirm-first, visually
 *   SECONDARY take-over ("I'll supply the documents myself").
 *
 * - **AM branch** (``evidence_source === "am"``) — the AM took the run over, so
 *   supplying the declarations page is now REQUIRED. A required dropzone plus
 *   an optional other-documents dropzone, an inline refusal, and a
 *   confirm-first "Wait for the carrier instead" revert when the payload offers
 *   it. "Check now" no longer applies and the substrate drops it from the
 *   payload, so ``HitlSideActions`` renders nothing.
 *
 * **Take-over and revert are NOT ``x-actions``** and are never rendered by
 * ``HitlSideActions`` — they are confirm-first controls drawn here, and neither
 * is ever the default or ``autoFocus``. ``HitlSideActions`` stays the Check-now
 * renderer only; no id filtering, because those ids are not in the payload.
 *
 * All three actions ride the ONE substrate answer transport the hosts already
 * use (``POST …/resume`` → ``resume_workflow``). There is no second product
 * route and no host-local resolution path (design Constraint 17).
 */

/** The declarations page, in the ONE wire shape the substrate's answer
 *  boundary and both probe families agree on. */
interface EncodedDeclarationsDocument {
  filename: string;
  mime_type: string;
  content_b64: string;
}

export interface PendingEdocsDrawerBodyProps {
  sections: DrawerSectionMap;
  /** The gate's HITL question — carries the branch, the ``x-actions``
   *  ("Check now") on the carrier branch, and the required-evidence keys on
   *  the AM branch. */
  hitlQuestion?: Record<string, unknown> | null;
  /** Runs a declared side action (Check now) by its endpoint. */
  onSideAction?: (endpoint: string) => Promise<SideActionResult>;
  /** Submits the reject decision (terminates the workflow). */
  onReject?: () => void;
  /** True while a reject submit is in flight — disables the confirm button. */
  rejectPending?: boolean;
  /** Error from the most recent failed reject submit — shown inline next to
   *  the confirm controls (the top-level action-error banner is suppressed
   *  whenever a hitlQuestion is present, which is always true here). */
  rejectError?: string | null;
  /**
   * Changes which side supplies the evidence. The host maps the action onto
   * its EXISTING resume submitter exactly as it already does for reject —
   * ``(action) => handleResume({ decision: action })``. Nothing here needs a
   * new transport.
   */
  onEvidenceSourceChange?: (
    action: "takeover" | "wait_for_carrier",
  ) => void | Promise<void>;
  /**
   * Submits the AM's required declarations page. Receives the COMPLETE inner
   * resume payload, already nested — the host hands it straight to the same
   * resume submitter without reshaping it.
   *
   * The nested ``field_values.edoc_declarations_document`` shape is built HERE,
   * in one place, deliberately: spreading it across the two hosts is how the
   * two drift, and the substrate's ``_normalize_declarations_document`` treats
   * the flat ``policy_documents`` emit only as a fallback.
   */
  onSubmitEvidence?: (userInput: Record<string, unknown>) => void | Promise<void>;
  /** True while an evidence or source-change submit is in flight. */
  evidencePending?: boolean;
  /** Refusal from the answer boundary (e.g. "…the declarations page is
   *  required…"), rendered INLINE next to the submit control. */
  evidenceSubmitError?: string | null;
  /**
   * The phase / milestone main stream (running-drawer). A ReactNode, not a
   * snapshot: the bodies stay presentation-only and none of them imports
   * `phaseModel.ts`, so the adapter keeps its single-reader rule.
   *
   * Rendered FIRST when present, and it SUPPRESSES the flat steps chip it
   * replaces.
   */
  phaseStream?: ReactNode | null;
  /**
   * The step view (story 26739) — the "what do I do right now" pane for this
   * moment in the run. A ReactNode, not a model: the bodies stay
   * presentation-only, so the host derives the view from `push.status` on
   * every render and hands the finished node down.
   *
   * When present it takes the hero position and suppresses the "Needs your
   * input" banner and the flat steps chip. It does NOT replace the decision /
   * status region below: that region owns "Check now", the confirm-first
   * Reject and — on the AM branch — THE evidence uploader, and there is
   * exactly one of those in the product. So the packet view is rendered ABOVE
   * it and the host passes it no uploader of its own. Deliberate deviation
   * from artboard (4), which draws the dropzone inside the pane: a second
   * dropzone is the failure this body already fixed once.
   *
   * Default `undefined` reproduces today's markup exactly.
   */
  stepView?: ReactNode | null;
  /**
   * The carrier-evidence decision card (running-drawer). Present ONLY when the
   * run carries an `evidence_decision`, never gated on a status literal —
   * gating on the parked status makes the early-override path unreachable
   * (AC-6). Absent prop reproduces today's markup byte for byte.
   */
  decisionCard?: ReactNode | null;
  /** The run record shown behind the more-information entry point. The SAME
   *  record the standalone /workflows/runs/<id> page renders. */
  record: RunRecordBodyProps;
  /** Copy-id / kill-session links — More information pane only. */
  debugActions?: ReactNode | null;
  /** Whether the more-information pane is open. Owned by the top-level
   *  drawer component (survives step/phase updates); the entry-point button
   *  itself lives in the sticky footer, not here. */
  moreInfoOpen?: boolean;
  /** Called when the pane's own back button is clicked. */
  onMoreInfoClose?: () => void;
}

const ACCEPTED_FILE_TYPES = [".pdf", ".png", ".jpg", ".jpeg", ".docx", ".doc"];
const MAX_FILE_BYTES = 25 * 1024 * 1024;

function getString(
  q: Record<string, unknown> | null | undefined,
  key: string,
): string | undefined {
  const v = q?.[key];
  return typeof v === "string" && v ? v : undefined;
}

async function encodeDeclarations(
  file: File,
): Promise<EncodedDeclarationsDocument> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve({
        filename: file.name,
        mime_type: file.type || "application/pdf",
        content_b64: comma >= 0 ? result.slice(comma + 1) : result,
      });
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("FileReader failed"));
    reader.readAsDataURL(file);
  });
}

export function PendingEdocsDrawerBody({
  sections,
  hitlQuestion,
  onSideAction,
  onReject,
  rejectPending = false,
  rejectError,
  onEvidenceSourceChange,
  onSubmitEvidence,
  evidencePending = false,
  evidenceSubmitError,
  phaseStream,
  stepView,
  decisionCard,
  record,
  debugActions,
  moreInfoOpen = false,
  onMoreInfoClose,
}: PendingEdocsDrawerBodyProps) {
  const [confirmingReject, setConfirmingReject] = useState(false);
  const [confirmingTakeover, setConfirmingTakeover] = useState(false);
  const [confirmingRevert, setConfirmingRevert] = useState(false);
  const [declarationsFiles, setDeclarationsFiles] = useState<File[]>([]);
  const [otherFiles, setOtherFiles] = useState<File[]>([]);
  const [localError, setLocalError] = useState<string>("");

  // The AM-branch keys are ABSENT on the carrier branch by design: their key
  // NAMES contain "required", and the must_pass probes grep the raw
  // hitl_question::text — keys included — to prove the pre-takeover body never
  // says it. So every read here treats absence as "not required"/empty.
  // Never `=== false`.
  const amBranch = hitlQuestion?.["evidence_source"] === "am";
  const requiredDocuments =
    (hitlQuestion?.["required_documents"] as string[] | undefined) ?? [];
  const offerWaitForCarrier = Boolean(hitlQuestion?.["offer_wait_for_carrier"]);
  const declarationsLabel =
    getString(hitlQuestion, "policy_label") ?? "Declarations Page (required)";
  const otherLabel =
    getString(hitlQuestion, "other_label") ?? "Other Documents (optional)";

  // Submit is gated on the DECLARATIONS bucket alone. Other-documents-only must
  // never enable it — that is the one behaviour HitlFileUploadDecisionPanel's
  // canApprove (true if EITHER bucket has a file) gets wrong for this gate.
  const canSubmitEvidence =
    declarationsFiles.length > 0 && !evidencePending && !localError;

  const inlineError = localError || evidenceSubmitError || "";

  // Named at staging time, not at fan-out time: "it reached Epic but not the
  // email" is only useful to the AM BEFORE they walk away. See
  // EMAIL_ATTACHABLE_MAX_BYTES for why this is not the same as MAX_FILE_BYTES.
  const tooBigForEmail = [...declarationsFiles, ...otherFiles].filter(
    (f) => f.size > EMAIL_ATTACHABLE_MAX_BYTES,
  );

  const makeFileHandler =
    (bucket: File[], setBucket: (next: File[]) => void) =>
    (incoming: FileList) => {
      setLocalError("");
      const accepted: File[] = [];
      const oversized: string[] = [];
      Array.from(incoming).forEach((f) => {
        if (f.size > MAX_FILE_BYTES) oversized.push(f.name);
        else accepted.push(f);
      });
      if (oversized.length > 0) {
        const list = oversized.map((n) => `"${n}"`).join(", ");
        setLocalError(
          `${list} exceeded the 25 MB per-file limit and ${
            oversized.length === 1 ? "was" : "were"
          } skipped.`,
        );
      }
      const combined = [...bucket, ...accepted];
      if (combined.length > MAX_DOCUMENTS) {
        setLocalError(`Up to ${MAX_DOCUMENTS} files per bucket.`);
        return;
      }
      setBucket(combined);
    };

  const submitEvidence = async () => {
    if (!canSubmitEvidence || !onSubmitEvidence) return;
    try {
      // ALL of them. This used to encode `declarationsFiles[0]` alone, so a
      // second declarations page was accepted by the dropzone and dropped on
      // the way out.
      const declarations = await Promise.all(
        declarationsFiles.map(encodeDeclarations),
      );
      // `other_documents` is sent for the first time here. The bucket has
      // always collected files and never put them in the payload — accepted,
      // then silently discarded. It rides `field_values.other_documents`, the
      // same key `file_upload_decision` uses, so the substrate's existing
      // reader picks it up with no new wire spelling.
      const other = await Promise.all(otherFiles.map(encodeDeclarations));
      // The COMPLETE inner resume payload, nested — built once, here.
      // `edoc_volume_path` is deliberately NOT sent: the substrate's
      // resolve_decpage_documents prefers uploaded bytes, and there is no
      // carrier-delivered path on this branch to point at.
      await Promise.resolve(
        onSubmitEvidence({
          decision: "approve",
          field_values: {
            edoc_declarations_document: declarations,
            ...(other.length > 0 ? { other_documents: other } : {}),
          },
        }),
      );
    } catch (err) {
      setLocalError(
        err instanceof Error ? err.message : "Could not read the file.",
      );
    }
  };

  // Split into named pieces so the running-drawer decision card can REPLACE
  // the copy while the two live affordances are re-parented rather than
  // deleted. `checkNowBlock` and the Reject block are children of the calm
  // card's container today, so a naive replacement would remove both with it.
  const checkNowBlock: ReactNode =
    hitlQuestion && onSideAction ? (
      <HitlSideActions hitlQuestion={hitlQuestion} onAction={onSideAction} />
    ) : null;

  const carrierStatusCard: ReactNode = (
    <>
      <p className="text-sm font-semibold text-primary-gray-light-800">
        Waiting on the carrier
      </p>
      <p className="text-xs text-primary-gray-light-700">
        Waiting for the carrier to deliver the updated policy documents into
        Epic. We check every day automatically, and this resumes on its own
        when they arrive — there&rsquo;s nothing you need to do here.
      </p>
      <p className="text-2xs text-primary-gray-light-500">
        This can take up to 14 days.
      </p>

      {/* "Check now" side action (from the gate's x-actions). Absent on the
          AM branch because the substrate drops x-actions there. */}
      {checkNowBlock}

      {/* Take over — a THIRD, visually secondary option. Never the default,
          never autoFocus, and confirm-first because it makes supplying the
          declarations page the AM's job. */}
      {onEvidenceSourceChange &&
        (confirmingTakeover ? (
          <div
            className="mt-1 flex items-center gap-2 flex-wrap"
            role="dialog"
            aria-label="Confirm taking over the documents"
            data-testid="pending-edocs-takeover-confirm"
          >
            <span className="text-2xs text-primary-gray-light-700">
              Take this over? You&rsquo;ll need to supply the declarations page
              yourself, and we&rsquo;ll stop waiting on the carrier.
            </span>
            <button
              type="button"
              onClick={() => setConfirmingTakeover(false)}
              className="lnk-gray text-2xs px-1.5 py-0.5"
              data-testid="pending-edocs-takeover-cancel"
            >
              Keep waiting
            </button>
            <button
              type="button"
              disabled={evidencePending}
              onClick={() => {
                void onEvidenceSourceChange("takeover");
              }}
              className="px-2 py-0.5 rounded border border-primary-gray-light-300 bg-base-white text-primary-gray-light-800 text-2xs font-semibold hover:bg-primary-gray-light-50 disabled:opacity-60"
              data-testid="pending-edocs-takeover-yes"
            >
              {evidencePending ? "…" : "Yes, I'll supply them"}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmingTakeover(true)}
            className="mt-1 self-start text-2xs text-primary-gray-light-700 hover:underline"
            data-testid="pending-edocs-takeover"
          >
            I&rsquo;ll supply the documents myself
          </button>
        ))}
    </>
  );

  // The AM branch's dropzones + submit + inline refusal, extracted so the
  // decision card can replace the branch COPY without taking the uploader
  // with it.
  const amEvidenceUploader: ReactNode = (
    <>
      <div className="mt-1 flex flex-col gap-2">
        <div>
          <p
            className="text-2xs font-semibold text-primary-gray-light-800 mb-1"
            id="edoc-declarations-label"
          >
            {declarationsLabel}
          </p>
          <div
            role="group"
            aria-labelledby="edoc-declarations-label"
            data-testid="pending-edocs-declarations-dropzone"
          >
            <FileUploadDropzone
              compact
              acceptedFileTypes={ACCEPTED_FILE_TYPES}
              fileUploadMaxSize={MAX_FILE_BYTES}
              maxFiles={MAX_DOCUMENTS}
              uploadedFiles={declarationsFiles as unknown as FileList}
              onFileChange={makeFileHandler(
                declarationsFiles,
                setDeclarationsFiles,
              )}
              onFileRemove={(index: number) => {
                setLocalError("");
                setDeclarationsFiles(
                  declarationsFiles.filter((_, i) => i !== index),
                );
              }}
            />
          </div>
          {declarationsFiles.length > 0 && (
            <p
              className="text-2xs text-primary-gray-light-700 mt-1"
              data-testid="pending-edocs-declarations-selected"
            >
              {declarationsFiles.map((f) => f.name).join(", ")}
            </p>
          )}
          {tooBigForEmail.length > 0 && (
            <p
              role="status"
              className="text-2xs text-primary-warning-700 mt-1"
              data-testid="pending-edocs-email-size-warning"
            >
              {tooBigForEmail.map((f) => f.name).join(", ")}
              {tooBigForEmail.length === 1 ? " is" : " are"} too large to attach
              to the reply email.{" "}
              {tooBigForEmail.length === 1 ? "It" : "They"} will still reach
              Epic — send {tooBigForEmail.length === 1 ? "it" : "them"} to the
              client another way.
            </p>
          )}
        </div>

        <div>
          <p
            className="text-2xs font-semibold text-primary-gray-light-800 mb-1"
            id="edoc-other-label"
          >
            {otherLabel}
          </p>
          <div
            role="group"
            aria-labelledby="edoc-other-label"
            data-testid="pending-edocs-other-dropzone"
          >
            <FileUploadDropzone
              compact
              acceptedFileTypes={ACCEPTED_FILE_TYPES}
              fileUploadMaxSize={MAX_FILE_BYTES}
              uploadedFiles={otherFiles as unknown as FileList}
              onFileChange={makeFileHandler(otherFiles, setOtherFiles)}
              onFileRemove={(index: number) => {
                setOtherFiles(otherFiles.filter((_, i) => i !== index));
              }}
            />
          </div>
        </div>
      </div>

      {/* Submit + the INLINE refusal, side by side. Not a top-level banner:
          the top-level action-error banner is already suppressed whenever a
          hitlQuestion is present, which is always true here. */}
      <div className="mt-1 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          disabled={!canSubmitEvidence}
          onClick={() => {
            void submitEvidence();
          }}
          className="px-2 py-0.5 rounded bg-primary-brand-600 text-base-white text-2xs font-semibold hover:bg-primary-brand-700 disabled:opacity-60"
          data-testid="pending-edocs-evidence-submit"
        >
          {evidencePending ? "…" : "Submit declarations page"}
        </button>
        {inlineError && (
          <span
            role="alert"
            className="text-2xs text-primary-error-700"
            data-testid="pending-edocs-evidence-error"
          >
            {inlineError}
          </span>
        )}
      </div>
    </>
  );

  const amEvidenceCard: ReactNode = (
    <>
      <p className="text-sm font-semibold text-primary-gray-light-800">
        {renderInlineBold(
          getString(hitlQuestion, "question") ?? "Supply the Declarations Page",
        )}
      </p>
      <p className="text-xs text-primary-gray-light-700">
        {renderInlineBold(
          getString(hitlQuestion, "message") ??
            getString(hitlQuestion, "pending_action") ??
            "You took this over, so the declarations page is required before this can finish.",
        )}
      </p>
      {requiredDocuments.length > 0 && (
        <p
          className="text-2xs text-primary-gray-light-500"
          data-testid="pending-edocs-required-documents"
        >
          Required: {requiredDocuments.join(", ").replace(/_/g, " ")}
        </p>
      )}

      {amEvidenceUploader}

      {/* Revert — offered only while the substrate says it is still possible
          (the carrier still posts declarations and nothing is attached).
          Confirm-first, and never an x-action. */}
      {onEvidenceSourceChange &&
        offerWaitForCarrier &&
        (confirmingRevert ? (
          <div
            className="mt-1 flex items-center gap-2 flex-wrap"
            role="dialog"
            aria-label="Confirm waiting for the carrier instead"
            data-testid="pending-edocs-revert-confirm"
          >
            <span className="text-2xs text-primary-gray-light-700">
              Hand this back to the carrier? We&rsquo;ll go back to waiting for
              their documents.
            </span>
            <button
              type="button"
              onClick={() => setConfirmingRevert(false)}
              className="lnk-gray text-2xs px-1.5 py-0.5"
              data-testid="pending-edocs-revert-cancel"
            >
              Never mind
            </button>
            <button
              type="button"
              disabled={evidencePending}
              onClick={() => {
                void onEvidenceSourceChange("wait_for_carrier");
              }}
              className="px-2 py-0.5 rounded border border-primary-gray-light-300 bg-base-white text-primary-gray-light-800 text-2xs font-semibold hover:bg-primary-gray-light-50 disabled:opacity-60"
              data-testid="pending-edocs-revert-yes"
            >
              {evidencePending ? "…" : "Yes, wait for the carrier"}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmingRevert(true)}
            className="mt-1 self-start text-2xs text-primary-gray-light-700 hover:underline"
            data-testid="pending-edocs-revert"
          >
            Wait for the carrier instead
          </button>
        ))}
    </>
  );

  const rejectBlock: ReactNode = (
    <>
      {/* Confirm-first Reject — terminal, so guard against a stray click.
          Offered on BOTH branches. */}
      {onReject &&
        (confirmingReject ? (
          <div
            className="mt-1 flex items-center gap-2"
            role="dialog"
            aria-label="Confirm reject"
            data-testid="pending-edocs-reject-confirm"
          >
            <span className="text-2xs text-primary-gray-light-700">
              Reject this change? It won&rsquo;t be applied.
            </span>
            <button
              type="button"
              onClick={() => setConfirmingReject(false)}
              className="lnk-gray text-2xs px-1.5 py-0.5"
              data-testid="pending-edocs-reject-keep"
            >
              Keep
            </button>
            <button
              type="button"
              disabled={rejectPending}
              onClick={onReject}
              className="px-2 py-0.5 rounded bg-primary-error-600 text-base-white text-2xs font-semibold hover:bg-primary-error-700 disabled:opacity-60"
              data-testid="pending-edocs-reject-yes"
            >
              {rejectPending ? "…" : "Yes, reject"}
            </button>
            {rejectError && (
              <span
                role="alert"
                className="text-2xs text-primary-error-700"
                data-testid="pending-edocs-reject-error"
              >
                {rejectError}
              </span>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmingReject(true)}
            className="mt-1 self-start text-2xs text-primary-error-700 hover:underline"
            data-testid="pending-edocs-reject"
          >
            Reject if this change is no longer needed
          </button>
        ))}
    </>
  );

  return (
    <>
      {stepView ? null : phaseStream && amBranch ? (
        <div
          data-testid="hitl-needs-you-banner"
          role="status"
          className="mb-2 flex items-center gap-2 rounded-md border border-primary-warning-300 bg-primary-warning-50 px-3 py-2"
        >
          <span
            aria-hidden
            className="inline-block h-2 w-2 shrink-0 rounded-full bg-primary-warning-600"
          />
          <p className="text-2xs font-semibold uppercase tracking-wide text-primary-warning-800">
            {NEEDS_YOU_LABEL}
          </p>
        </div>
      ) : null}

      {/* The phase stream is the ALWAYS-ON progress display (operator
          directive 2026-09-16): a step view no longer displaces it. Both
          render, stream FIRST, so run progress stays the first thing in the
          body and the step view's reassurance sits under it. */}
      {phaseStream ?? null}
      {stepView ?? null}

      {decisionCard ? (
        /* running-drawer: the decision card REPLACES the calm status card
           entirely (AC-5 fails on a card PLUS a separate status element). The
           gate's two live affordances — "Check now" and the confirm-first
           Reject — plus the AM branch's uploader are RE-PARENTED into the same
           decision REGION, drawn as a continuation of the card, not deleted.
           They are children of that one region, so AC-7's four-region
           enumeration still counts exactly four. */
        <div
          className="mb-2 flex flex-col"
          data-testid="carrier-evidence-decision"
          data-evidence-source={amBranch ? "am" : "carrier"}
        >
          {decisionCard}
          <div
            className="-mt-2 rounded-b-lg border border-t-0 border-primary-gray-light-200 bg-base-white px-3 pb-3 pt-1 flex flex-col gap-1"
            data-testid="carrier-evidence-gate-actions"
          >
            {checkNowBlock}
            {/* NO uploader here. `CarrierEvidencePanel` (the decisionCard) owns
                the one evidence uploader now. This slot used to render a SECOND
                one — two dropzones and a "Submit declarations page" button,
                stacked under the card's own dropzone and "Stage evidence" — so
                the AM was offered two ways to hand over the same PDF. The
                flag-off path below still uses `amEvidenceUploader`, which has
                no card to carry the uploader for it. */}
            {rejectBlock}
          </div>
        </div>
      ) : (
        <div
          className="rounded-lg border border-primary-gray-light-200 bg-base-white p-3 mb-2 flex flex-col gap-1"
          data-testid="pending-edocs-status"
          data-evidence-source={amBranch ? "am" : "carrier"}
        >
          {amBranch ? amEvidenceCard : carrierStatusCard}

          {rejectBlock}
        </div>
      )}

      {sections.request ?? null}

      <MoreInformationSection
        record={record}
        open={moreInfoOpen}
        onClose={() => onMoreInfoClose?.()}
        extraBlocks={
          <>
            {debugActions ?? null}
          </>
        }
      />
    </>
  );
}

export default PendingEdocsDrawerBody;
